import ko from 'knockout';
import moment from 'moment';
import numeral from 'numeral';

import { QualityType, Competention, CostCentre } from './databaseModels';
import { resolveCdnUrl } from '../cdn-url-resolver';
import { dataUriToBlob } from '../file-helper';
import { lookupAddress } from '../postalCodeApi';
import { CustomError } from '../fetch';
import { SelectPicker } from '../custom-packages/choicesjs/selectPicker';
import { FileInputModel, FileModel } from "./file";

const INT32_MAX_VALUE = 2147483647;

interface RequestModelOptions {
    companyAvatarHash: string;
    minimumRate: number;
    companyHasOwnHourlyRate: boolean;
}

interface PreviewModel {
    title: string;
    subTitle: string;
    imageSrc: string;
    dateText?: string;
    timeText?: string;
    hourlyRateText: string;
    cityText: string;
}

export class RequestModel {
    id: KnockoutObservable<string>;
    options: RequestModelOptions;

    name: KnockoutObservable<string>;
    amount: KnockoutObservable<string>;
    hourlyRate: KnockoutObservable<string>;
    companyHasOwnHourlyRate: KnockoutObservable<boolean>;

    enableEmployeeClocks: KnockoutObservable<boolean>;

    forFavorites: KnockoutObservable<boolean>;
    companyHasFavoriteEmployees: KnockoutObservable<boolean>;

    multipleDays: KnockoutObservable<boolean>;
    date: KnockoutObservable<moment.Moment>;
    dateTo: KnockoutObservable<moment.Moment>;

    timePartFrom: KnockoutObservable<string>;
    timePartTo: KnockoutObservable<string>;
    requestDuration: KnockoutComputed<number | null>;

    selectedQualityType: KnockoutObservable<string>;
    selectedCompetentions: KnockoutObservableArray<string>;

    qualityTypes: KnockoutObservableArray<QualityType>;
    qualityTypesExpanded: KnockoutObservable<boolean>;
    qualityTypesToShow: KnockoutComputed<QualityType[]>;
    competentions: KnockoutObservableArray<Competention>;
    hasAcceptedRegistrations: KnockoutObservable<boolean>;

    imageInfoExpanded: KnockoutObservable<boolean>;
    image: KnockoutObservable<any>;
    imageHash: KnockoutObservable<string>;

    extraInfo: KnockoutObservable<string>;

    addressInfoExpanded: KnockoutObservable<boolean>;
    address: KnockoutObservable<string>;
    houseNumber: KnockoutObservable<string>;
    postalCode: KnockoutObservable<string>;
    city: KnockoutObservable<string>;

    imageSrc: KnockoutComputed<any>;
    selectedQualityTypeName: KnockoutComputed<string>;
    filteredCompetentions: KnockoutComputed<Competention[]>;

    showInfoButton: KnockoutComputed<boolean>;
    showTooltip: KnockoutObservable<boolean>;

    costCentres: KnockoutObservableArray<CostCentre>;
    showNewCostCentre: KnockoutObservable<boolean>;
    selectedCostCentre: KnockoutObservable<SelectPicker>;
    newCostCentre: KnockoutObservable<string>;

    addDocuments: KnockoutObservable<boolean>;
    documents: KnockoutObservableArray<FileInputModel>;
    existingDocuments: KnockoutObservableArray<FileModel>;

    errors: KnockoutObservableArray<CustomError>;

    minimumRate: KnockoutObservable<number>;

    certificateRequired: KnockoutObservable<boolean>;
    workFromHome: KnockoutObservable<boolean>;
    isReacurring: KnockoutObservable<boolean>;
    hoursAvailableMin: KnockoutObservable<number>;
    hoursAvailableMax: KnockoutObservable<number>;
    closeDate: KnockoutObservable<moment.Moment>;
    previewImageSrc: KnockoutComputed<string>;
    oneTimeEmploymentPreview: KnockoutComputed<PreviewModel>;
    recurringEmploymentPreview: KnockoutComputed<PreviewModel>;

    constructor(model: any, options: RequestModelOptions) {
        const self = this;

        this.options = options;
        this.errors = ko.observableArray<CustomError>();
        this.qualityTypes = ko.observableArray<QualityType>(model.qualityTypes);
        this.qualityTypesExpanded = ko.observable<boolean>(false);
        this.qualityTypesToShow = ko.pureComputed(() => {
            if (this.qualityTypesExpanded()) {
                return this.qualityTypes();
            }

            const array = this.qualityTypes().filter((qt, index) => index < 3 || qt.id == this.selectedQualityType());

            return array;
        });

        this.costCentres = ko.observableArray<CostCentre>(model.costCentres);
        this.showNewCostCentre = ko.observable<boolean>(false);
        this.selectedCostCentre = ko.observable<SelectPicker>().extend({
            errorHandler: { source: this.errors, id: 'costCentreId' }
        });
        this.newCostCentre = ko.observable<string>().extend({
            errorHandler: { source: this.errors, id: 'newCostCentre' }
        });

        if (model.costCentreId != null || model.defaultCostCentreId != null) {
            const costCentreId = model.costCentreId ?? model.defaultCostCentreId;
            const costCentre = this.costCentres().find((c: CostCentre) => c.id === costCentreId);
            if (costCentre != null) {
                this.selectedCostCentre({
                    label: costCentre.name,
                    value: costCentre.id
                });
            }
        }

        this.documents = ko.observableArray<FileInputModel>([new FileInputModel()]);
        this.existingDocuments = ko.observableArray<FileModel>(model.existingDocuments ?? model.documents?.map((doc: any) => new FileModel(doc)) ?? []);
        this.addDocuments = ko.observable<boolean>(this.existingDocuments().length > 0);

        this.competentions = ko.observableArray<Competention>(model.competentions);

        this.companyHasFavoriteEmployees = ko.observable(model.companyHasFavoriteEmployees);

        this.hasAcceptedRegistrations = ko.observable(model.hasAcceptedRegistrations);
        this.id = ko.observable<string>(model.id);

        this.amount = ko.observable<string>(model.amount)
            .extend({
                required: {
                    params: true,
                    message: 'Aantal is verplicht'
                },
                validation: {
                    validator: (val: string) => {
                        const numberValue = Number(val);

                        return numberValue > 0 && numberValue < INT32_MAX_VALUE;
                    },
                    message: 'Vul een geldig aantal in'
                },
                errorHandler: { source: this.errors, id: 'Amount' }
            });

        this.companyHasOwnHourlyRate = ko.observable<boolean>(this.options.companyHasOwnHourlyRate);

        this.minimumRate = ko.observable<number>(this.options.minimumRate);

        this.hourlyRate = ko.observable<string>(this.companyHasOwnHourlyRate() ? model.hourlyRate : this.options.minimumRate)
            .extend({
                required: {
                    params: true,
                    message: 'Uurtarief is verplicht'
                },
                validation: [
                    {
                        validator: (val: string) => {
                            return numeral.validate(val, 'nl-nl');
                        },
                        message: 'Vul een geldig uurtarief in'
                    },
                    {
                        validator: (val: string) => {
                            const numberValue = numeral(val);

                            return numberValue.value() >= this.minimumRate();
                        },
                        message: () => `Uurtarief moet minstens €${numeral(this.minimumRate()).format('0,0.00')} per uur zijn`
                    }
                ]
            });

        this.multipleDays = ko.observable<boolean>(model.dateTo != null);

        this.date = ko.observable<moment.Moment>(model.date || moment())
            .extend({
                errorHandler: { source: this.errors, id: 'StartDate' }
            });
        this.date.subscribe(async (value) => {
            await this.loadCompanyMinimumRateForDate(value);
        });
        this.dateTo = ko.observable<moment.Moment>(model.dateTo ?? moment())
            .extend({
                errorHandler: { source: this.errors, id: 'EndDate' },
                validation: [
                    {
                        validator: (val: string) => {
                            if (val == null) {
                                return true;
                            }
                            const dateTo = moment(val, 'DD-MM-YYYY');
                            const date = moment(this.date());
                            return dateTo.isSameOrAfter(date, 'days');
                        },
                        message: 'Tot-datum mag niet vóór Van-datum liggen.'
                    }
                ]
            });

        this.isReacurring = ko.observable<boolean>(model.isReacurring ?? true);

        this.timePartFrom = ko.observable<string>(model.timePartFrom)
            .extend({
                errorHandler: { source: this.errors, id: 'TimePartFrom' }
            });
        this.timePartTo = ko.observable<string>(model.timePartTo)
            .extend({
                errorHandler: { source: this.errors, id: 'TimePartTo' }
            });

        this.requestDuration = ko.pureComputed<number | null>(() => {
            if (this.timePartFrom() == null || this.timePartTo() == null) {
                return null;
            }

            const startDate = this.getStartDate();
            const endDate = this.getEndDate();
            const duration = moment.duration(moment(endDate!).diff(startDate!)).asHours();
            return duration;
        });

        this.selectedQualityType = ko.observable<string>(model.selectedQualityType)
            .extend({
                required: {
                    params: true,
                    message: 'Selecteer een type medewerker'
                }
            });
        this.name = ko.observable<string>(model.name || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een naam voor de vacature in'
                }
            });


        this.addressInfoExpanded = ko.observable<boolean>(model.addressInfoExpanded || false);
        this.address = ko.observable<string>(model.address || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een adres voor de vacature in'
                },
                errorHandler: { source: this.errors, id: 'Address' }
            });
        this.houseNumber = ko.observable<string>(model.houseNumber || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een huisnummer voor de vacature in'
                }
            });
        this.postalCode = ko.observable<string>(model.postalCode || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een postcode voor de vacature in'
                }
            });
        this.city = ko.observable<string>(model.city || '')
            .extend({
                required: {
                    params: true,
                    message: 'Voer een stad voor de vacature in'
                }
            });

        this.selectedQualityType.subscribe(() => this.selectedCompetentions([]));

        this.selectedCompetentions = ko.observableArray<string>(model.selectedCompetentions ? [...model.selectedCompetentions] : []);
        this.enableEmployeeClocks = ko.observable<boolean>(model.enableEmployeeClocks || true);

        this.forFavorites = ko.observable<boolean>(model.forFavorites || false);

        this.imageInfoExpanded = ko.observable<boolean>(model.imageInfoExpanded || false);
        this.imageHash = ko.observable<string>(model.imageHash);
        this.image = ko.observable<any>({
            dataURL: ko.observable<any>(model.imageDataURL)
        });

        this.image().dataURL.subscribe(() => {
            this.imageHash('');
        });

        if (this.imageHash()) {
            this.image().file = ko.observable({ name: ' ' });
        }

        this.imageSrc = ko.pureComputed(() => {
            if (this.image().dataURL()) {
                return this.image().dataURL();
            }

            if (this.imageHash()) {
                return resolveCdnUrl(this.imageHash());
            }

            return '';
        });

        this.selectedQualityTypeName = ko.pureComputed(() => {
            return this.qualityTypes().find((qt: QualityType) => qt.id === this.selectedQualityType())!.name;
        });

        this.filteredCompetentions = ko.pureComputed(() => {
            const competentions = this.competentions().filter((c: Competention) => this.selectedQualityType()
                && (c.qualityTypeIds && c.qualityTypeIds.includes(this.selectedQualityType())))
                .sort((a: Competention, b: Competention) => a.examOrder - b.examOrder);
            return competentions;
        });

        this.extraInfo = ko.observable<string>(model.extraInfo || '');

        this.showTooltip = ko.observable(false);
        this.showInfoButton = ko.pureComputed(() => {
            return this.hourlyRate() != null && numeral(this.hourlyRate()).value() < 21.50;
        });

        this.certificateRequired = ko.observable<boolean>(model.certificateRequired ?? false);
        this.workFromHome = ko.observable<boolean>(model.workFromHome ?? false);
        this.hoursAvailableMin = ko.observable<number>(model.hoursAvailableMin)
            .extend({
                errorHandler: { source: this.errors, id: 'HoursAvailableMin' }
            });
        this.hoursAvailableMax = ko.observable<number>(model.hoursAvailableMax)
            .extend({
                errorHandler: { source: this.errors, id: 'HoursAvailableMax' }
            });
        this.closeDate = ko.observable<moment.Moment>(model.closeDate ?? moment())
            .extend({
                errorHandler: { source: this.errors, id: 'CloseDate' }
            });

        this.previewImageSrc = ko.pureComputed(() => {
            const companyAvatarHash = resolveCdnUrl(this.options.companyAvatarHash);
            return this.imageInfoExpanded() && this.image().dataURL() ? this.imageSrc() : companyAvatarHash;
        });

        this.oneTimeEmploymentPreview = ko.pureComputed<PreviewModel>(() => {
            const startTime = this.timePartFrom() !== undefined ? this.timePartFrom() : 'Starttijd';
            const endTime = this.timePartTo() !== undefined ? this.timePartTo() : 'Eindtijd';

            return {
                title: this.name(),
                subTitle: this.selectedQualityType() ? this.selectedQualityTypeName() : 'Type medewerker',
                imageSrc: this.previewImageSrc(),
                hourlyRateText: `${numeral(this.hourlyRate()).format('$0,0.00')}/u`,
                cityText: this.city(),
                dateText: this.date().format('dd D MMM'),
                timeText: `${startTime} - ${endTime}`,
            } as PreviewModel;
        });

        this.recurringEmploymentPreview = ko.pureComputed<PreviewModel>(() => {
            const hoursAvailableMin = this.hoursAvailableMin() != null ? this.hoursAvailableMin() : '';
            const hoursAvailableMax = this.hoursAvailableMax() != null ? this.hoursAvailableMax() : '';

            const hoursAvailable = hoursAvailableMin !== '' && hoursAvailableMax !== ''
                ? `${hoursAvailableMin} - ${hoursAvailableMax}`
                : hoursAvailableMin
                    ? `${hoursAvailableMin} - Max`
                    : hoursAvailableMax
                        ? `Min - ${hoursAvailableMax}`
                        : '';

            const selectedQualityTypeName = this.selectedQualityType() ? this.selectedQualityTypeName() : 'Type medewerker';
            const title = `${selectedQualityTypeName} ${hoursAvailable !== '' ? `(${hoursAvailable} uur)` : '(uren beschikbaar)'}`

            const startDateText = moment(this.date()).format('D MMM');
            const endDateText = moment(this.dateTo()).format('D MMM');
            const dateText = `${startDateText} - ${endDateText}`;

            return {
                title: title,
                dateText: dateText,
                imageSrc: this.previewImageSrc(),
                hourlyRateText: `${numeral(this.hourlyRate()).format('$0,0.00')}/u`,
                cityText: this.city(),
            } as PreviewModel;
        });
    }

    async onLeavePostalCodeOrHouseNumberField() {
        if (this.postalCode() && this.postalCode().length > 0
            && this.houseNumber() && this.houseNumber().length > 0) {
            const result = await lookupAddress(this.postalCode(), this.houseNumber());

            if (result.success) {
                this.address(result.street);
                this.city(result.town);
            }
        }
    }

    toggleTooltip() {
        this.showTooltip(!this.showTooltip());
    }

    toggleCostCentre() {
        this.showNewCostCentre(!this.showNewCostCentre());
    }

    getStartDate(): moment.Moment | null {
        return this.getDate(true);
    }

    getEndDate(): moment.Moment | null {
        return this.getDate(false);
    }

    isDateFilledIn(): boolean {
        return this.date() !== undefined;
    }

    formatDate(): string {
        let date: moment.Moment = this.getStartDate()!;
        return date
            ? this.isReacurring()
                ? `${date.format('DD/MM/YYYY')} - ${this.dateTo().format('DD/MM/YYYY')}`
                : date.format('dddd DD/MM/YYYY')
            : '';
    }

    formatTime(): string {
        const startDate = this.getStartDate();
        const endDate = this.getEndDate();

        if (!startDate || !endDate) {
            return '';
        }

        return `${startDate.format('HH:mm')} - ${endDate.format('HH:mm')}`;
    }

    formatRate(): string {
        return `€${numeral(this.hourlyRate()).format('0,0.00')}/u`;
    }

    formatSelectedCompetentions(): string {
        let selectedCompetentionDescriptions = this.selectedCompetentions()
            .filter((cId: string) => this.competentions().find((c: Competention) => c.id === cId)!.qualityTypeIds.includes(this.selectedQualityType()))
            .map((cId: string) => this.competentions()
                .find((c: Competention) => c.id === cId)!.description);

        return selectedCompetentionDescriptions.length === 0
            ? 'Geen'
            : selectedCompetentionDescriptions.join(', ');
    }

    addDocument() {
        const currentDocuments = this.documents();
        currentDocuments.push(new FileInputModel());
        this.documents(currentDocuments);
    }

    removeDocument(index: number) {
        const currentDocuments = this.documents();
        currentDocuments.splice(index, 1);
        this.documents(currentDocuments);
    }

    removeExistingDocument(index: number) {
        const currentExistingDocuments = this.existingDocuments();
        currentExistingDocuments.splice(index, 1);
        this.existingDocuments(currentExistingDocuments);
    }

    async loadCompanyMinimumRateForDate(date: moment.Moment) {
        const response = await fetch(`/CompanyMinimumRate/Rate?date=${date.format('YYYY-MM-DD')}`);

        if (response.ok) {
            const minimumRate = await response.json();
            this.minimumRate(minimumRate);
        }

        return this.options.minimumRate;
    }

    copy(): RequestModel {
        return new RequestModel({
            qualityTypes: this.qualityTypes(),
            competentions: this.competentions(),
            id: this.id(),
            amount: this.amount(),
            hourlyRate: this.hourlyRate(),
            date: this.date(),
            timePartFrom: this.timePartFrom(),
            timePartTo: this.timePartTo(),
            selectedQualityType: this.selectedQualityType(),
            selectedCompetentions: this.selectedCompetentions(),
            enableEmployeeClocks: this.enableEmployeeClocks(),
            forFavorites: this.forFavorites(),
            imageInfoExpanded: this.imageInfoExpanded(),
            imageDataURL: this.image().dataURL(),
            imageHash: this.imageHash(),
            name: this.name(),
            extraInfo: this.extraInfo(),
            addressInfoExpanded: this.addressInfoExpanded(),
            address: this.address(),
            houseNumber: this.houseNumber(),
            city: this.city(),
            postalCode: this.postalCode(),
            costCentres: this.costCentres(),
            costCentreId: this.selectedCostCentre()?.value,
            documents: this.existingDocuments().map((doc) => ({ id: doc.id(), name: doc.name() })),
            isReacurring: this.isReacurring(),
            dateTo: this.dateTo(),
            certificateRequired: this.certificateRequired(),
            workFromHome: this.workFromHome(),
            hoursAvailableMin: this.hoursAvailableMin(),
            hoursAvailableMax: this.hoursAvailableMax(),
            closeDate: this.closeDate()
        }, this.options);
    }

    toRequestData(): any {
        const startDate = this.getStartDate()
            ? this.getStartDate()!.format('YYYY-MM-DD HH:mm')
            : '';
        const endDate = this.isReacurring()
            ? this.dateTo()?.format('YYYY-MM-DD') ?? ''
            : this.getEndDate()
                ? this.getEndDate()!.format('YYYY-MM-DD HH:mm')
                : '';

        const data = new FormData();
        data.append('id', this.id());
        data.append('name', this.name());
        data.append('amount', this.amount());
        data.append('hourlyRate', numeral(this.hourlyRate()).format('0.00'));
        data.append('startDate', startDate);
        data.append('endDate', endDate);

        if (!this.isReacurring() && this.multipleDays() && this.dateTo() != null) {
            data.append('toDate', this.dateTo().format('YYYY-MM-DD'));
        }

        data.append('qualityTypeId', this.selectedQualityType());
        data.append('enableEmployeeClocks', this.enableEmployeeClocks().toString());
        data.append('forFavorites', this.forFavorites().toString());
        data.append('address', this.address());
        data.append('city', this.city());
        data.append('houseNumber', this.houseNumber());
        data.append('postalCode', this.postalCode());

        data.append('certificateRequired', this.certificateRequired().toString());

        data.append('workFromHome', this.workFromHome().toString());
        data.append('isReacurring', this.isReacurring().toString());
        if (this.isReacurring()) {
            if (this.hoursAvailableMin() != null) {
                data.append('hoursAvailableMin', this.hoursAvailableMin().toString());
            }
            if (this.hoursAvailableMax() != null) {
                data.append('hoursAvailableMax', this.hoursAvailableMax().toString());
            }
            if (this.closeDate() != null) {
                data.append('closeDate', this.closeDate().format('YYYY-MM-DD'));
            }
        }

        if (this.imageInfoExpanded()) {
            if (this.image().file && this.image().file()) {
                data.append('image', this.image().file());
            } else if (this.image().dataURL()) {
                const blob = dataUriToBlob(this.image().dataURL());
                data.append('image', blob);
            } else if (this.imageHash() != null && this.imageHash() != '') {
                fetch(this.imageSrc())
                    .then(res => res.blob())
                    .then(blob => {
                        data.append('image', blob);
                    });
            }
            data.append('imageHash', this.imageHash());
        }

        this.extraInfo() != null && data.append('extraInfo', this.extraInfo());
        if (this.selectedCompetentions().length > 0) {
            this.selectedCompetentions().forEach((c: string) => data.append('competentions[]', c));
        }

        if (this.showNewCostCentre() && this.newCostCentre() != null) {
            data.append('newCostCentre', this.newCostCentre());
        } else if (!this.showNewCostCentre() && this.selectedCostCentre() != null) {
            data.append('costCentreId', this.selectedCostCentre().value);
        }

        if (this.addDocuments()) {
            this.documents().forEach((doc) => {
                if (doc.file().file() != null) {
                    data.append('documents', doc.file().file());
                }
            });

            this.existingDocuments().forEach((doc) => {
                data.append('existingDocumentIds', doc.id());
            });
        }

        return data;
    }

    private getDate(startDate: boolean): moment.Moment | null {
        if (!this.date()) {
            return null;
        }

        const timeParts = startDate
            ? [this.timePartFrom(), this.timePartTo()]
            : [this.timePartTo(), this.timePartFrom()];

        const dateFormat = `${this.date().format('MM-DD-YYYY')}`;
        let date1 = moment(`${dateFormat} ${timeParts[0]}`, 'MM-DD-YYYY HH:mm');
        const date2 = moment(`${dateFormat} ${timeParts[1]}`, 'MM-DD-YYYY HH:mm');


        //If the enddate's time part is before the startdate's time part, we can assume the end date is the next day
        if (!startDate && date1.isBefore(date2, 'hour')) {
            date1 = date1.add(1, 'day');
        }

        if (!date1.isValid()) {
            return null;
        }

        return date1;
    }
}

